import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip, Box} from "@chakra-ui/react";
import TableStructure from "../../CommonComponents/TableStructure";
import { getAllStaticWordsData } from "../../services/StaticWords";
import { setPreview } from "../../redux/actions/previewActions";

const StaticWordsTable = () => {
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState("asc"); // Sorting state
  const session = useSelector((state) => state.session);
  const dispatch = useDispatch();
  const limit = 50; // Number of records per page

  // Fetch static words from the API
  const fetchStaticWords = async (page, term) => {
    setLoading(true);
    try {
      const result = await getAllStaticWordsData(limit, page, term);
      if (result?.data) {
        setRecords(formatRecords(result?.data?.data || []));
        setTotalPages(Math.ceil(result?.data?.total / limit));
        setTotalRecords(result?.data?.total);

        if (result.data.page_number && parseInt(result.data.page_number) !== currentPage) {
          setCurrentPage(parseInt(result.data.page_number));
        }
      }
    } catch (error) {
      console.error("Error fetching static words:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatRecords = (data) => {
    return data?.map((item) => {
      const record = {
        key: item?.key,
        _id: item?._id
      };

      // Format all translations as "lang_code: value" in a single field
      if (item?.languages && Array.isArray(item.languages)) {
        const translationsText = item.languages.map(lang => 
          `${lang?.lang_code || ' '}: ${lang?.value || ' '}`
        ).join('\n');
        
        record.translations = translationsText;
        record.full_translations = translationsText; // For tooltip
      } else {
        record.translations = ' ';
        record.full_translations = ' ';
      }

      return record;
    })
  }

  const columns = [
    {
      label: "Key",
      accessor: "key",
    },
    {
      label: "Translations",
      accessor: "translations",
      width: "70%",
      render: (value, record) => (
        <Tooltip 
          label={<Box whiteSpace="pre-line">{record.full_translations}</Box>}         
          placement="top"
          hasArrow
          bg="gray.700"
          color="white"
          fontSize="sm"
          px={3}
          py={5}
          borderRadius="md"
          isDisabled={!record.full_translations}
        >
          <span style={{ cursor: 'pointer', whiteSpace: 'pre-line' }}>{value}</span>
        </Tooltip>
      )
    },
  ]

  // Handle search button click
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false; // Skip the first render
      setCurrentPage(1); // Reset to page 1 for a new search
      setSearchText(searchTerm);
      fetchStaticWords(1, searchTerm);
    }
  };

  const sortRecords = () => {
    // const newSortOrder = sortOrder === "asc" ? "desc" : "asc"; // Update sortOrder first
    // setSortOrder(newSortOrder);
setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    const sorted = [...records].sort((a, b) =>
      sortOrder === "asc"
        ? a.key.localeCompare(b.key)
        : b.key.localeCompare(a.key)
    );

    setRecords(sorted);
  };

  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false; // Skip the first render
      fetchStaticWords(currentPage, searchText);
      return;
    }
    fetchStaticWords(currentPage, searchText);
    dispatch(setPreview(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, dispatch]);

  const handleClear = () => {
    setSearchText('');
    fetchStaticWords(1, "");
  };

  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  return (
    <TableStructure
      isAuthenticated={session?.isAuthenticated}
      handleSearch={handleSearch}
      handleClear={handleClear}
      totalRecords={totalRecords}
      handlePageChange={handlePageChange}
      currentPage={currentPage}
      totalPages={totalPages}
      sortRecords={sortRecords}
      sortOrder={sortOrder}
      loading={loading}
      records={records}
      type="static-words"
      title="Static Words"
      addOption
      columns={columns}
    />
  );
};

export default StaticWordsTable;
